import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Copy, Edit, Plus, RefreshCw, Printer } from 'lucide-react';
import { useAuth } from '../../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../../contexts/I18nProvider';
import { stcBinService } from '../../../../../../services/stcBinService';
import { stcZoneService } from '../../../../../../services/stcZoneService';
import { stcBinTypeService } from '../../../../../../services/stcBinTypeService';
import { stcBinSizeService } from '../../../../../../services/stcBinSizeService';
import { stcBinGrpService } from '../../../../../../services/stcBinGrpService';
import { stcResourceService } from '../../../../../../services/stcResourceService';
import { stcPriorityService } from '../../../../../../services/stcPriorityService';
import { stcMovementTypeService } from '../../../../../../services/stcMovementTypeService';
import { warehouseService } from '../../../../../../services/warehouseService';
import { StcBinPagedDto, StcBinsPagedQueryParams, UpdateStcBinRequest } from '../../../../../../types/stcBin';
import { EnrichedBin, MasterDataLookups } from './types';
import { BulkUpdateModal } from '../../../rack-builder/BulkUpdateModal';
import { StcBinCopyPropertiesModal } from './StcBinCopyPropertiesModal';
import { LocationCreateModal } from './LocationCreateModal';
import { ReceiptBinWarningModal } from './ReceiptBinWarning';
import { isReceiptBinPickConflict } from '../../../../../../utils/receiptBinWarning';
import { LabelPrintModal } from '../../../../../common/LabelPrintModal';
import { WarehouseTable, useInlineEdit } from '../../../../../shared/table';
import { useStatusOptions } from '../../../../../common/statusPresentation';
import { createColumnFactory } from '../../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction, ServerFetchParams } from '../../../../../shared/table/types/table';

interface LocationsDataTableProps {
  warehouseID?: string;
}

/** Sentinel: brukeren avbrøt lagring i mottaksadvarselen — ikke en feil. */
const RECEIPT_WARNING_CANCELLED = new Error('receipt-bin-warning-cancelled');

export const LocationsDataTable: React.FC<LocationsDataTableProps> = ({ warehouseID }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo || '';

  // Server-side pagination state
  const [serverBins, setServerBins] = useState<EnrichedBin[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [masterData, setMasterData] = useState<MasterDataLookups | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBinCodes, setSelectedBinCodes] = useState<string[]>([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCopyPropsModal, setShowCopyPropsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  // Advarsel mottakslokasjon plukkbar/reserverbar (TW 36401238) — onSave venter på brukerens svar
  const [receiptWarning, setReceiptWarning] = useState<{ binCode: string; resolve: (proceed: boolean) => void } | null>(null);

  // Track whether initial load is done (to avoid full-screen spinner on filter changes)
  const initialLoadDoneRef = useRef(false);
  // Monotonisk teller — forkaster utdaterte svar (siste fetch vinner) i stedet for å droppe
  // overlappende fetch. Viktig fordi dropdown-valg kan fyre tett (rask klikking).
  const fetchIdRef = useRef(0);
  // Store latest fetch params for refresh after edit/delete
  const latestFetchParamsRef = useRef<StcBinsPagedQueryParams>({ pageNumber: 1, pageSize: 50 });
  // Debounce timer for column filters
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Master data ref for use in fetch callback
  const masterDataRef = useRef<MasterDataLookups | null>(null);

  // Create the column factory
  const columnFactory = createColumnFactory<EnrichedBin>();

  // Lokasjonskategori (ST42): master-basert, språkbevisst — tekst/opsjoner fra StatusCodes (Model A).
  const binCategoryStatusOptions = useStatusOptions(42);
  const binCategoryTextMap = useMemo(
    () => new Map(binCategoryStatusOptions.map(o => [o.value, o.text])),
    [binCategoryStatusOptions]
  );

  // Enrich a server-paged bin with client-side master data lookups
  const enrichBin = useCallback((bin: StcBinPagedDto, lookups: MasterDataLookups | null): EnrichedBin => ({
    ...bin,
    // 4 descriptions come from server LEFT JOINs (kategori-tekst resolves i formatter fra ST42):
    zoneName: bin.zoneDescription,
    binTypeName: bin.binTypeDescription,
    binSizeName: bin.binSizeDescription,
    binGrpName: bin.binGrpDescription,
    // 5 descriptions resolved client-side from master data (not in server JOINs):
    resourceName: lookups && bin.resourceNo ? lookups.resources.get(bin.resourceNo)?.description1 : undefined,
    pickPriorityName: lookups && bin.pickPriorityNo ? lookups.priorities.get(bin.pickPriorityNo)?.description1 : undefined,
    putAwayPriorityName: lookups && bin.putAwayPriorityNo ? lookups.priorities.get(bin.putAwayPriorityNo)?.description1 : undefined,
    movementTypeName: lookups && bin.movementTypeNo ? lookups.movementTypes.get(bin.movementTypeNo)?.description1 : undefined,
    warehouseName: lookups && bin.warehouseID ? lookups.warehouses.get(bin.warehouseID)?.warehouseDescr : undefined,
  }), []);

  // Fetch paged bins from server
  const fetchPagedBins = useCallback(async (params: StcBinsPagedQueryParams) => {
    if (!companyNo) return;

    // Only show full loading spinner on initial load — subsequent fetches keep table visible (B1)
    if (!initialLoadDoneRef.current) {
      setIsLoading(true);
    }
    setError(null);

    // Tildel request-ID; nyere fetch som starter før denne er ferdig forkaster dette svaret
    // (siste filter vinner — ingen droppede valg ved rask klikking)
    const thisRequestId = ++fetchIdRef.current;
    try {
      // If warehouseID prop is set, add it as a filter
      const queryParams = warehouseID
        ? { ...params, warehouseID }
        : params;

      const result = await stcBinService.getCurrentPaged(queryParams);
      if (thisRequestId !== fetchIdRef.current) return; // utdatert svar — forkast

      const enriched: EnrichedBin[] = result.items.map((bin: StcBinPagedDto) =>
        enrichBin(bin, masterDataRef.current)
      );

      setServerBins(enriched);
      setTotalRecords(result.totalRecords);
      latestFetchParamsRef.current = params;
      initialLoadDoneRef.current = true;
    } catch (err) {
      console.error('Error loading paged bins:', err);
      if (thisRequestId === fetchIdRef.current) {
        setError(t('manager.modules.adminItemsLocations.locations.errorLoad', {}, 'Kunne ikke laste lokasjonsdata. Prøv igjen.'));
      }
    } finally {
      if (thisRequestId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [companyNo, warehouseID, enrichBin, t]);

  // Load master data (for dropdown edit options) — only once on mount
  const loadMasterData = useCallback(async () => {
    if (!companyNo) return;
    try {
      const [
        warehousesData,
        zonesData,
        binTypesData,
        binSizesData,
        binGroupsData,
        resourcesData,
        prioritiesData,
        movementTypesData
      ] = await Promise.all([
        warehouseService.getWarehouses(),
        stcZoneService.getCurrent(),
        stcBinTypeService.getCurrent(),
        stcBinSizeService.getCurrent(),
        stcBinGrpService.getCurrent(),
        stcResourceService.getCurrent(),
        stcPriorityService.getCurrent(),
        stcMovementTypeService.getCurrent()
      ]);

      const lookups: MasterDataLookups = {
        warehouses: new Map(warehousesData.map(w => [w.warehouseID, w])),
        zones: new Map(zonesData.map(z => [z.stcZoneID, z])),
        binTypes: new Map(binTypesData.map(t => [t.binTypeNo, t])),
        binSizes: new Map(binSizesData.map(s => [s.binSizeNo, s])),
        binGroups: new Map(binGroupsData.map(g => [g.binGrpNo, g])),
        resources: new Map(resourcesData.map(r => [r.resourceNo, r])),
        priorities: new Map(prioritiesData.map(p => [p.priorityNo, p])),
        movementTypes: new Map(movementTypesData.map(m => [m.movementTypeNo, m]))
      };

      setMasterData(lookups);
      masterDataRef.current = lookups;
    } catch (err) {
      console.error('Error loading master data for dropdowns:', err);
    }
  }, [companyNo]);

  // Server-side pagination callback — called by WarehouseTable when page/sort/filter changes
  const handleServerFetch = useCallback((params: ServerFetchParams) => {
    const f = params.filters;
    const d = params.dropdownFilters || {};

    // B3: flervalgs lookup-filter — dropdown sender koder komma-separert (IN), og overstyrer
    // fritekst for samme kolonne. Kolonnenøkler er kode-feltene.
    const joinCodes = (arr?: string[]) => (arr && arr.length > 0 ? arr.join(',') : undefined);
    const binGrpNos = joinCodes(d.binGrpNo);
    const stcBinCatNos = joinCodes(d.stcBinCatNo);
    const binTypeNos = joinCodes(d.binTypeNo);
    const binSizeNos = joinCodes(d.binSizeNo);
    const movementTypeNos = joinCodes(d.movementTypeNo);
    const pickPriorityNos = joinCodes(d.pickPriorityNo);
    const putAwayPriorityNos = joinCodes(d.putAwayPriorityNo);
    const resourceNos = joinCodes(d.resourceNo);
    const warehouseIDs = joinCodes(d.warehouseID);
    const stcZoneIDs = joinCodes(d.stcZoneID);

    const queryParams: StcBinsPagedQueryParams = {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      sortColumn: params.sortColumn,
      sortDirection: params.sortDirection,
      binCode: f?.binCode || undefined,
      description1: f?.description1 || undefined,
      // Lookup-felt: dropdown (IN) overstyrer fritekst
      warehouseID: warehouseIDs ? undefined : (f?.warehouseID || undefined),
      stcZoneID: stcZoneIDs ? undefined : (f?.stcZoneID || undefined),
      binGrpNo: binGrpNos ? undefined : (f?.binGrpNo || undefined),
      stcBinCatNo: stcBinCatNos ? undefined : (f?.stcBinCatNo || undefined),
      binTypeNo: binTypeNos ? undefined : (f?.binTypeNo || undefined),
      binSizeNo: binSizeNos ? undefined : (f?.binSizeNo || undefined),
      movementTypeNo: movementTypeNos ? undefined : (f?.movementTypeNo || undefined),
      resourceNo: resourceNos ? undefined : (f?.resourceNo || undefined),
      pickPriorityNo: pickPriorityNos ? undefined : (f?.pickPriorityNo || undefined),
      putAwayPriorityNo: putAwayPriorityNos ? undefined : (f?.putAwayPriorityNo || undefined),
      binGrpNos, stcBinCatNos, binTypeNos, binSizeNos,
      movementTypeNos, pickPriorityNos, putAwayPriorityNos,
      resourceNos, warehouseIDs, stcZoneIDs,
      customSortID: f?.customSortID || undefined,
      replenishMinLevel: f?.replenishMinLevel || undefined,
      replenishMaxLevel: f?.replenishMaxLevel || undefined,
      sharedBinMaxQuantity: f?.sharedBinMaxQuantity || undefined,
      pickCustomOrder: f?.pickCustomOrder || undefined,
      putAwayCustomOrder: f?.putAwayCustomOrder || undefined,
      itemNo: f?.itemNo || undefined,
      pickableYesNo: f?.pickableYesNo || undefined,
      reservationAllowed: f?.reservationAllowed || undefined,
      autoReplenishment: f?.autoReplenishment || undefined,
      negativeReservationAllowed: f?.negativeReservationAllowed || undefined,
      sharedBinYesNo: f?.sharedBinYesNo || undefined,
      sharedBinPreferEmptyBin: f?.sharedBinPreferEmptyBin || undefined,
      lastCountedDate: f?.lastCountedDate || undefined,
      lastMovementDate: f?.lastMovementDate || undefined,
    };

    // Debounce filter changes (300ms) but execute page/sort changes immediately
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }

    // Detect if any filter value changed (compared to previous fetch)
    const prevFilters = latestFetchParamsRef.current;
    const filterKeys: (keyof StcBinsPagedQueryParams)[] = [
      'binCode', 'description1', 'warehouseID', 'stcZoneID',
      'binGrpNo', 'stcBinCatNo', 'binTypeNo', 'binSizeNo',
      'movementTypeNo', 'resourceNo', 'pickPriorityNo', 'putAwayPriorityNo',
      'customSortID', 'replenishMinLevel', 'replenishMaxLevel', 'sharedBinMaxQuantity',
      'pickCustomOrder', 'putAwayCustomOrder', 'itemNo',
      'pickableYesNo', 'reservationAllowed', 'autoReplenishment',
      'negativeReservationAllowed', 'sharedBinYesNo', 'sharedBinPreferEmptyBin',
      'lastCountedDate', 'lastMovementDate',
      // B3: flervalgs lookup-filter (IN)
      'binGrpNos', 'stcBinCatNos', 'binTypeNos', 'binSizeNos',
      'movementTypeNos', 'pickPriorityNos', 'putAwayPriorityNos',
      'resourceNos', 'warehouseIDs', 'stcZoneIDs',
    ];
    const isFilterChange = params.pageNumber === 1 &&
      filterKeys.some(key => queryParams[key] !== prevFilters[key]);

    if (isFilterChange) {
      filterDebounceRef.current = setTimeout(() => {
        fetchPagedBins(queryParams);
      }, 300);
    } else {
      fetchPagedBins(queryParams);
    }
  }, [fetchPagedBins]);

  // Refresh helper — re-fetches with the latest params
  const refreshData = useCallback(() => {
    fetchPagedBins(latestFetchParamsRef.current);
  }, [fetchPagedBins]);

  // Initial load
  useEffect(() => {
    fetchPagedBins({ pageNumber: 1, pageSize: 50 });
    loadMasterData();
  }, [fetchPagedBins, loadMasterData]);

  // Re-berik allerede lastede bins når master-data ankommer. Data-henting og master-data lastes
  // i parallell ved oppstart; hvis bin-dataene kommer først, blir de klient-side oppslagene
  // (Ressurs, Prioritet plukk/utlegg, Bevegelseskode, Lagersted) beriket med null → mangler tekst
  // til man gjorde en aksjon. Dette fyller dem inn så snart master-data er tilgjengelig.
  useEffect(() => {
    if (!masterData) return;
    setServerBins(prev => prev.map(bin => enrichBin(bin, masterData)));
  }, [masterData, enrichBin]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }
    };
  }, []);

  // Inline editing
  const inlineEditConfig = useInlineEdit<EnrichedBin>({
    getRowId: (row) => row.binCode,
    onSave: async (_originalRow, editedData) => {
      if (isReceiptBinPickConflict({ ..._originalRow, ...editedData })) {
        const proceed = await new Promise<boolean>(resolve =>
          setReceiptWarning({ binCode: (editedData.binCode as string) || _originalRow.binCode, resolve })
        );
        setReceiptWarning(null);
        // Avbrutt: raden blir stående i redigering så brukeren kan rette flaggene
        if (!proceed) throw RECEIPT_WARNING_CANCELLED;
      }
      const request: UpdateStcBinRequest = {
        warehouseID: editedData.warehouseID as string | undefined,
        stcZoneID: editedData.stcZoneID as string | undefined,
        binCode: editedData.binCode as string | undefined,
        description1: editedData.description1,
        binSizeNo: editedData.binSizeNo as number,
        binTypeNo: editedData.binTypeNo as number,
        resourceNo: editedData.resourceNo as number | undefined,
        itemNo: editedData.itemNo as string | undefined,
        binGrpNo: editedData.binGrpNo as number | undefined,
        pickPriorityNo: editedData.pickPriorityNo as number | undefined,
        pickCustomOrder: editedData.pickCustomOrder as string | undefined,
        replenishMinLevel: editedData.replenishMinLevel as number | undefined,
        replenishMaxLevel: editedData.replenishMaxLevel as number | undefined,
        movementTypeNo: editedData.movementTypeNo as number | undefined,
        putAwayCustomOrder: editedData.putAwayCustomOrder as string | undefined,
        stcBinCatNo: editedData.stcBinCatNo as number | undefined,
        customSortID: editedData.customSortID as number | undefined,
        sharedBinYesNo: editedData.sharedBinYesNo as boolean | undefined,
        sharedBinMaxQuantity: editedData.sharedBinMaxQuantity as number | undefined,
        putAwayPriorityNo: editedData.putAwayPriorityNo as number | undefined,
        reservationAllowed: editedData.reservationAllowed as boolean | undefined,
        negativeReservationAllowed: editedData.negativeReservationAllowed as boolean | undefined,
        autoReplenishment: editedData.autoReplenishment as boolean | undefined,
        pickableYesNo: editedData.pickableYesNo as boolean | undefined,
        sharedBinPreferEmptyBin: editedData.sharedBinPreferEmptyBin as boolean | undefined,
        // Tri-state (TW 36344429): select-editoren gir string ('true'/'false'/'' = arv),
        // uendret verdi er boolean/null fra raden. null = arv fra profil.
        barCodeConfirmLocation: typeof editedData.barCodeConfirmLocation === 'string'
          ? (editedData.barCodeConfirmLocation === 'true' ? true
            : editedData.barCodeConfirmLocation === 'false' ? false
            : null)
          : editedData.barCodeConfirmLocation as boolean | null | undefined
      };
      await stcBinService.update(
        companyNo,
        _originalRow.warehouseID,
        _originalRow.binCode,
        request
      );
    },
    onSaveSuccess: () => refreshData(),
    onSaveError: (err) => {
      if (err === RECEIPT_WARNING_CANCELLED) return;
      setError(err.message || t('manager.modules.adminItemsLocations.locations.errorSave', {}, 'Feil ved lagring'));
    }
  });

  // Build dropdown options from master data for inline editing
  const zoneOptions = useMemo(() =>
    masterData ? Array.from(masterData.zones.values()).map(z => ({
      value: z.stcZoneID, label: `${z.stcZoneID} - ${z.description1}`
    })) : [], [masterData]);

  const binGrpOptions = useMemo(() =>
    masterData ? Array.from(masterData.binGroups.values()).map(g => ({
      value: g.binGrpNo, label: `${g.binGrpNo} - ${g.description1}`
    })) : [], [masterData]);

  const binTypeOptions = useMemo(() =>
    masterData ? Array.from(masterData.binTypes.values()).map(t => ({
      value: t.binTypeNo, label: `${t.binTypeNo} - ${t.description1}`
    })) : [], [masterData]);

  const binSizeOptions = useMemo(() =>
    masterData ? Array.from(masterData.binSizes.values()).map(s => ({
      value: s.binSizeNo, label: `${s.binSizeNo} - ${s.description1}`
    })) : [], [masterData]);

  // Master-StatusCodes (ST42) — label er alt «kode - tekst», språkbevisst.
  const binCategoryOptions = useMemo(() =>
    binCategoryStatusOptions.map(o => ({ value: o.value, label: o.label })),
    [binCategoryStatusOptions]);

  const resourceOptions = useMemo(() =>
    masterData ? Array.from(masterData.resources.values()).map(r => ({
      value: r.resourceNo, label: `${r.resourceNo} - ${r.description1}`
    })) : [], [masterData]);

  const priorityOptions = useMemo(() =>
    masterData ? Array.from(masterData.priorities.values()).map(p => ({
      value: p.priorityNo, label: `${p.priorityNo} - ${p.description1}`
    })) : [], [masterData]);

  const movementTypeOptions = useMemo(() =>
    masterData ? Array.from(masterData.movementTypes.values()).map(m => ({
      value: m.movementTypeNo, label: `${m.movementTypeNo} - ${m.description1}`
    })) : [], [masterData]);

  const warehouseOptions = useMemo(() =>
    masterData ? Array.from(masterData.warehouses.values()).map(w => ({
      value: w.warehouseID, label: `${w.warehouseID} - ${w.warehouseDescr}`
    })) : [], [masterData]);

  // B3: valglister for flervalgs lookup-filter (kombinert syntetisk datasett — ColumnDropdownFilter
  // leser item[column.key] per kolonne og hopper over null/undefined). Hver rad bærer kode + navn
  // for én dimensjon (prioritet-rader dekker både plukk og utlegg siden samme kilde).
  const dropdownOptionsData = useMemo<EnrichedBin[]>(() => {
    if (!masterData) return [];
    const rows: EnrichedBin[] = [];
    masterData.warehouses.forEach(w => rows.push({ warehouseID: w.warehouseID, warehouseName: w.warehouseDescr } as unknown as EnrichedBin));
    masterData.zones.forEach(z => rows.push({ stcZoneID: z.stcZoneID, zoneName: z.description1 } as unknown as EnrichedBin));
    masterData.resources.forEach(r => rows.push({ resourceNo: r.resourceNo, resourceName: r.description1 } as unknown as EnrichedBin));
    masterData.binGroups.forEach(g => rows.push({ binGrpNo: g.binGrpNo, binGrpName: g.description1 } as unknown as EnrichedBin));
    masterData.binTypes.forEach(bt => rows.push({ binTypeNo: bt.binTypeNo, binTypeName: bt.description1 } as unknown as EnrichedBin));
    masterData.binSizes.forEach(s => rows.push({ binSizeNo: s.binSizeNo, binSizeName: s.description1 } as unknown as EnrichedBin));
    // Kategori (ST42): kun koden trengs — formatter resolver tekst fra binCategoryTextMap.
    binCategoryStatusOptions.forEach(o => rows.push({ stcBinCatNo: o.value } as unknown as EnrichedBin));
    masterData.movementTypes.forEach(m => rows.push({ movementTypeNo: m.movementTypeNo, movementTypeName: m.description1 } as unknown as EnrichedBin));
    masterData.priorities.forEach(p => rows.push({
      pickPriorityNo: p.priorityNo, pickPriorityName: p.description1,
      putAwayPriorityNo: p.priorityNo, putAwayPriorityName: p.description1,
    } as unknown as EnrichedBin));
    return rows;
  }, [masterData, binCategoryStatusOptions]);

  // Handle bulk edit button
  const handleBulkEdit = useCallback(() => {
    if (selectedBinCodes.length === 0) {
      alert(t('manager.modules.adminItemsLocations.locations.selectAtLeastOne', {}, 'Velg minst én lokasjon for å redigere'));
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedBinCodes.length, t]);

  // Handle create new location
  const handleCreateNew = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  // Handle modal update success
  const handleUpdateSuccess = async () => {
    refreshData();
    setSelectedBinCodes([]);
    setShowBulkEditModal(false);
  };

  // Handle modal delete success
  const handleDeleteSuccess = async () => {
    refreshData();
    setSelectedBinCodes([]);
    setShowBulkEditModal(false);
  };

  // Handle create success
  const handleCreateSuccess = async () => {
    refreshData();
    setShowCreateModal(false);
  };

  const handleCopyPropsSuccess = async () => {
    refreshData();
    setSelectedBinCodes([]);
    setShowCopyPropsModal(false);
  };

  // Column definitions using the factory
  const columns: ColumnDefinition<EnrichedBin>[] = useMemo(() => [
    columnFactory.createTextColumn('warehouseID', 'warehouseID', t('manager.modules.adminItemsLocations.locations.colWarehouse', {}, 'Lagersted'), {
      minWidth: 150,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any, item: EnrichedBin) => (value !== null && value !== undefined && value !== '') ? `${value} - ${item.warehouseName || ''}` : '-',
      editable: true,
      editType: 'select',
      editOptions: warehouseOptions
    }),

    columnFactory.createTextColumn('stcZoneID', 'stcZoneID', t('warehouse.general.zone', {}, 'Sone'), {
      minWidth: 150,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any, item: EnrichedBin) => (value !== null && value !== undefined && value !== '') ? `${value} - ${item.zoneName || ''}` : '-',
      editable: true,
      editType: 'select',
      editOptions: zoneOptions
    }),

    columnFactory.createTextColumn('binCode', 'binCode', t('warehouse.general.bin', {}, 'Lokasjon'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
      minWidth: 200,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createNumberColumn('binGrpNo', 'binGrpNo', t('manager.modules.adminItemsLocations.locations.colBinGrp', {}, 'Lok.Grp'), {
      minWidth: 150,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any, item: EnrichedBin) => (value !== null && value !== undefined && value !== '') ? `${value} - ${item.binGrpName || ''}` : '-',
      editable: true,
      editType: 'select',
      editOptions: binGrpOptions
    }),

    columnFactory.createNumberColumn('binTypeNo', 'binTypeNo', t('common.fields.type', {}, 'Type'), {
      minWidth: 150,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any, item: EnrichedBin) => (value !== null && value !== undefined && value !== '') ? `${value} - ${item.binTypeName || ''}` : '-',
      editable: true,
      editType: 'select',
      editOptions: binTypeOptions
    }),

    columnFactory.createNumberColumn('binSizeNo', 'binSizeNo', t('manager.modules.adminItemsLocations.locations.colBinSize', {}, 'Størrelse'), {
      minWidth: 120,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any, item: EnrichedBin) => (value !== null && value !== undefined && value !== '') ? `${value} - ${item.binSizeName || ''}` : '-',
      editable: true,
      editType: 'select',
      editOptions: binSizeOptions
    }),

    columnFactory.createNumberColumn('stcBinCatNo', 'stcBinCatNo', t('manager.modules.adminItemsLocations.locations.colBinCategory', {}, 'Lok.Kategori'), {
      minWidth: 150,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any) => (value !== null && value !== undefined && value !== '') ? `${value} - ${binCategoryTextMap.get(Number(value)) || ''}` : '-',
      editable: true,
      editType: 'select',
      editOptions: binCategoryOptions
    }),

    columnFactory.createNumberColumn('movementTypeNo', 'movementTypeNo', t('manager.modules.adminItemsLocations.locations.colMovementType', {}, 'Bevegelseskode'), {
      minWidth: 160,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any, item: EnrichedBin) => (value !== null && value !== undefined && value !== '') ? `${value} - ${item.movementTypeName || ''}` : '-',
      editable: true,
      editType: 'select',
      editOptions: movementTypeOptions
    }),

    columnFactory.createNumberColumn('resourceNo', 'resourceNo', t('manager.modules.adminItemsLocations.locations.colResource', {}, 'Ressurs'), {
      minWidth: 150,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any, item: EnrichedBin) => (value !== null && value !== undefined && value !== '') ? `${value} - ${item.resourceName || ''}` : '-',
      editable: true,
      editType: 'select',
      editOptions: resourceOptions
    }),

    columnFactory.createBooleanColumn('reservationAllowed', 'reservationAllowed', t('manager.modules.adminItemsLocations.locations.colReservable', {}, 'Reserverbar'), {
      minWidth: 110,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createBooleanColumn('autoReplenishment', 'autoReplenishment', t('manager.modules.adminItemsLocations.locations.colAutoReplenish', {}, 'Automatisk påfylling'), {
      minWidth: 160,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createBooleanColumn('pickableYesNo', 'pickableYesNo', t('manager.modules.adminItemsLocations.locations.colPickable', {}, 'Plukklokasjon'), {
      minWidth: 130,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createBooleanColumn('negativeReservationAllowed', 'negativeReservationAllowed', t('manager.modules.adminItemsLocations.locations.colNegativeStock', {}, 'Negativ beholdning gyldig'), {
      minWidth: 190,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createDateColumn('lastCountedDate', 'lastCountedDate', t('manager.modules.adminItemsLocations.locations.colLastCounted', {}, 'Siste telledato'), {
      minWidth: 140,
      sortable: true,
      filterType: 'date'
    }),

    columnFactory.createDateColumn('lastMovementDate', 'lastMovementDate', t('manager.modules.adminItemsLocations.locations.colLastMovement', {}, 'Siste transaksjonsdato'), {
      minWidth: 180,
      sortable: true,
      filterType: 'date'
    }),

    columnFactory.createNumberColumn('pickPriorityNo', 'pickPriorityNo', t('manager.modules.adminItemsLocations.locations.colPickPriority', {}, 'Prioritet, plukk'), {
      minWidth: 140,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any, item: EnrichedBin) => (value !== null && value !== undefined && value !== '') ? `${value} - ${item.pickPriorityName || ''}` : '-',
      editable: true,
      editType: 'select',
      editOptions: priorityOptions
    }),

    columnFactory.createNumberColumn('putAwayPriorityNo', 'putAwayPriorityNo', t('manager.modules.adminItemsLocations.locations.colPutAwayPriority', {}, 'Prioritet, utlegg'), {
      minWidth: 150,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any, item: EnrichedBin) => (value !== null && value !== undefined && value !== '') ? `${value} - ${item.putAwayPriorityName || ''}` : '-',
      editable: true,
      editType: 'select',
      editOptions: priorityOptions
    }),

    columnFactory.createNumberColumn('customSortID', 'customSortID', t('manager.modules.adminItemsLocations.locations.colCustomSort', {}, 'Sortering'), {
      minWidth: 100,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('pickCustomOrder', 'pickCustomOrder', t('manager.modules.adminItemsLocations.locations.colPickOrder', {}, 'Plukkrekkefølge'), {
      minWidth: 140,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('putAwayCustomOrder', 'putAwayCustomOrder', t('manager.modules.adminItemsLocations.locations.colPutAwayOrder', {}, 'Innlagringsrekkefølge'), {
      minWidth: 160,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createNumberColumn('replenishMinLevel', 'replenishMinLevel', t('manager.modules.adminItemsLocations.locations.colReplenishMin', {}, 'Påfylling min %'), {
      minWidth: 130,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createNumberColumn('replenishMaxLevel', 'replenishMaxLevel', t('manager.modules.adminItemsLocations.locations.colReplenishMax', {}, 'Påfylling maks %'), {
      minWidth: 140,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('itemNo', 'itemNo', t('warehouse.general.itemNumber', {}, 'Varenr.'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createBooleanColumn('sharedBinYesNo', 'sharedBinYesNo', t('manager.modules.adminItemsLocations.locations.colSharedBin', {}, 'FellesLokasjon'), {
      minWidth: 130,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createNumberColumn('sharedBinMaxQuantity', 'sharedBinMaxQuantity', t('manager.modules.adminItemsLocations.locations.colSharedMaxQty', {}, 'Maks ant. på felleslok.'), {
      minWidth: 180,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createBooleanColumn('sharedBinPreferEmptyBin', 'sharedBinPreferEmptyBin', t('manager.modules.adminItemsLocations.locations.colPreferEmptyShared', {}, 'Prioriter tomme felleslok.'), {
      minWidth: 200,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    // Unntaksflagg lokasjonsskann (TW 36344429): tri-state — Arv (NULL) / Ja / Nei
    columnFactory.createTextColumn('barCodeConfirmLocation', 'barCodeConfirmLocation', t('manager.modules.adminItemsLocations.locations.colBarCodeConfirmLocation', {}, 'Krev lokasjonsskann'), {
      minWidth: 180,
      sortable: false,
      filterType: 'none',
      formatter: (value: any) => value === true
        ? t('manager.modules.adminItemsLocations.locations.scanFlagYes', {}, 'Ja')
        : value === false
          ? t('manager.modules.adminItemsLocations.locations.scanFlagNo', {}, 'Nei')
          : t('manager.modules.adminItemsLocations.locations.scanFlagInherit', {}, 'Arv (fra profil)'),
      editable: true,
      editType: 'select',
      editOptions: [
        { value: '', label: t('manager.modules.adminItemsLocations.locations.scanFlagInherit', {}, 'Arv (fra profil)') },
        { value: 'true', label: t('manager.modules.adminItemsLocations.locations.scanFlagYes', {}, 'Ja') },
        { value: 'false', label: t('manager.modules.adminItemsLocations.locations.scanFlagNo', {}, 'Nei') }
      ]
    })
  ], [binGrpOptions, binTypeOptions, binSizeOptions, binCategoryOptions, binCategoryTextMap, resourceOptions, priorityOptions, movementTypeOptions, warehouseOptions, zoneOptions, t]);

  // Custom row ID function
  const getRowId = useCallback((item: EnrichedBin) => item.binCode, []);

  // Handle selection change
  const handleSelectionChange = useCallback((selectedIds: (string | number)[], selectedData: EnrichedBin[]) => {
    setSelectedBinCodes(selectedIds.map(id => String(id)));
  }, []);

  // Table actions for bulk operations
  const tableActions: TableAction<EnrichedBin>[] = useMemo(() => [
    {
      id: 'create',
      label: t('manager.modules.adminItemsLocations.locations.actionNewLocation', {}, 'Ny lokasjon'),
      icon: Plus,
      onClick: () => handleCreateNew(),
      variant: 'primary'
    },
    {
      id: 'print-labels',
      label: t('manager.modules.adminItemsLocations.locations.actionPrintLabels', { '0': selectedBinCodes.length }, `Skriv ut etiketter (${selectedBinCodes.length})`),
      icon: Printer,
      onClick: () => setShowPrintModal(true),
      disabled: (_data: EnrichedBin[], _selected?: EnrichedBin[]) => selectedBinCodes.length === 0,
      variant: 'secondary'
    },
    {
      id: 'bulk-edit',
      label: t('manager.modules.adminItemsLocations.locations.actionMaintenance', { '0': selectedBinCodes.length }, `Vedlikehold (${selectedBinCodes.length})`),
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: (data: EnrichedBin[], selected?: EnrichedBin[]) => selectedBinCodes.length === 0,
      variant: 'primary'
    },
    {
      id: 'copy-properties',
      label: t('manager.modules.adminItemsLocations.locations.actionCopyProperties', { '0': selectedBinCodes.length }, `Kopier egenskaper (${selectedBinCodes.length})`),
      icon: Copy,
      onClick: () => setShowCopyPropsModal(true),
      disabled: (_data: EnrichedBin[], _selected?: EnrichedBin[]) => selectedBinCodes.length === 0,
      variant: 'secondary'
    },
    {
      id: 'refresh',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: () => refreshData(),
      variant: 'secondary'
    }
  ], [selectedBinCodes.length, handleBulkEdit, handleCreateNew, refreshData, t]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={serverBins}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: false,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'locations-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'current-page'
            }
          }}
          serverPagination={{
            enabled: true,
            totalRecords: totalRecords,
            onFetchData: handleServerFetch
          }}
          selectedRows={selectedBinCodes}
          onSelectionChange={handleSelectionChange}
          getRowId={getRowId}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          enableDropdownFilters={true}
          enableEmptyFilters={true}
          dropdownOptionsData={dropdownOptionsData.length > 0 ? dropdownOptionsData : undefined}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEditModal && selectedBinCodes.length > 0 && (() => {
        const selectedWarehouseID = serverBins.find(b => b.binCode === selectedBinCodes[0])?.warehouseID || '';

        return (
          <BulkUpdateModal
            isOpen={showBulkEditModal}
            onClose={() => setShowBulkEditModal(false)}
            selectedBinCodes={selectedBinCodes}
            warehouseID={selectedWarehouseID}
            filterOptions={null}
            onUpdateSuccess={handleUpdateSuccess}
            onDeleteSuccess={handleDeleteSuccess}
            selectedBinData={serverBins.filter(b => selectedBinCodes.includes(b.binCode))}
          />
        );
      })()}

      {/* Copy Properties Modal */}
      {showCopyPropsModal && selectedBinCodes.length > 0 && (() => {
        const targets = serverBins.filter(b => selectedBinCodes.includes(b.binCode));
        const selectedWarehouseID = targets[0]?.warehouseID || '';
        return (
          <StcBinCopyPropertiesModal
            isOpen={showCopyPropsModal}
            onClose={() => setShowCopyPropsModal(false)}
            selectedBinCodes={selectedBinCodes}
            selectedBinData={targets}
            companyNo={companyNo}
            warehouseID={selectedWarehouseID}
            onSuccess={handleCopyPropsSuccess}
          />
        );
      })()}

      {/* Create Modal */}
      {showCreateModal && (
        <LocationCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          masterData={masterData}
        />
      )}

      {/* Advarsel: mottakslokasjon plukkbar/reserverbar (inline-redigering) */}
      <ReceiptBinWarningModal
        isOpen={receiptWarning !== null}
        binCodes={receiptWarning ? [receiptWarning.binCode] : undefined}
        onConfirm={() => receiptWarning?.resolve(true)}
        onCancel={() => receiptWarning?.resolve(false)}
      />

      {/* Print Labels Modal */}
      {showPrintModal && selectedBinCodes.length > 0 && (
        <LabelPrintModal
          isOpen={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          printType="location"
          items={serverBins
            .filter(b => selectedBinCodes.includes(b.binCode))
            .map(b => ({
              id: b.binCode,
              displayName: b.description1 ? `${b.binCode} - ${b.description1}` : b.binCode,
              binCode: b.binCode,
              locationName: b.binCode,
              warehouseId: b.warehouseID,
              zoneName: b.zoneName,
              description: b.description1,
            }))}
        />
      )}
    </div>
  );
};
